import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Group,
  Modal,
  Paper,
  Stack,
  Text,
  Textarea,
  Accordion,
  Anchor,
  CopyButton,
  Tooltip,
  Skeleton,
  ActionIcon,
  Divider,
  ScrollArea
} from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import {
  IconHistory,
  IconCheck,
  IconChevronDown,
  IconCopy,
  IconDownload,
  IconEdit,
  IconEye,
  IconThumbUp,
  IconThumbDown
} from '@tabler/icons-react';
import DataTables from 'components/DataTables';
import Dates from 'components/Dates';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'hooks/useAuth';
import { masterPlanService } from 'services/dacum/masterPlan.service';
import {
  MasterPlanDetailByGroupResponse,
  CurriculumPayload,
  MasterPlanVersion
} from 'types/dacum/masterPlan';
import { showErrorNotification, showSuccessNotification } from 'utils/notification';
import { pathnames } from 'routes/dacum';
import * as XLSX from 'xlsx';

interface MasterPlanDetailByGroupProps {
  jobFamilyGroup: string;
  year: string;
  endYear?: string;
}

const MasterPlanDetailByGroup = ({
  jobFamilyGroup,
  year,
  endYear
}: MasterPlanDetailByGroupProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  const [rejectReason, setRejectReason] = React.useState('');
  const [rejectOpened, { open: openRejectModal, close: closeRejectModal }] = useDisclosure(false);
  const [versionOverviewOpened, { open: openVersionOverview, close: closeVersionOverview }] =
    useDisclosure(false);
  const [selectedJobFamilyIndex, setSelectedJobFamilyIndex] = React.useState(0);
  const [selectedMasterplanIndex, setSelectedMasterplanIndex] = React.useState(0);
  const [currentMasterplanId, setCurrentMasterplanId] = React.useState<string>('');
  const isMobile = useMediaQuery('(max-width: 768px)');

  const {
    data: responseData,
    isPending,
    error
  }: {
    data?: MasterPlanDetailByGroupResponse;
    isPending: boolean;
    error: unknown;
  } = useQuery({
    queryKey: ['masterPlanDetailByGroup', jobFamilyGroup, year, endYear],
    queryFn: () =>
      masterPlanService.getMasterPlanDetailByGroup({
        jobFamilyGroup,
        year,
        endYear: endYear || ''
      }),
    enabled: Boolean(jobFamilyGroup && year)
  });

  // Extract the selected job family and masterplan
  const jobFamilyList = responseData?.data || [];
  const selectedJobFamily = jobFamilyList[selectedJobFamilyIndex];
  const masterplanList = selectedJobFamily?.masterplans || [];
  const data = masterplanList[selectedMasterplanIndex];

  const showJobFamilyNavigation = true; // Always show
  const showMasterplanNavigation = true; // Always show

  const jobPositionCompetencyCount = React.useMemo(() => {
    const curriculumList = data?.curriculumList || [];
    const countMap = new Map<string, { name: string; competencies: Set<string> }>();

    curriculumList.forEach((curriculum: CurriculumPayload) => {
      const positions = curriculum.jobPositions || [];
      if (!Array.isArray(positions) || positions.length === 0) return;

      positions.forEach((pos: any) => {
        const positionName =
          typeof pos === 'object' && pos !== null
            ? pos.name
              ? String(pos.name)
              : String(pos.id || pos)
            : String(pos);
        const positionKey = positionName;

        if (!countMap.has(positionKey)) {
          countMap.set(positionKey, { name: positionName, competencies: new Set() });
        }
        countMap.get(positionKey)!.competencies.add(curriculum.competencyName);
      });
    });

    return Array.from(countMap.entries())
      .map(([id, data]) => ({
        id,
        name: data.name,
        count: data.competencies.size,
        competencies: Array.from(data.competencies)
      }))
      .sort((a, b) => b.count - a.count);
  }, [data?.curriculumList]);

  // Update current masterplan ID when selection changes
  React.useEffect(() => {
    if (data?.msDACUMProgramID) {
      setCurrentMasterplanId(data.msDACUMProgramID);
    }
  }, [data?.msDACUMProgramID]);

  const curriculumColumns = React.useMemo(
    () => [
      {
        heading: 'Competency',
        id: 'competencyName',
        sortable: true,
        active: true,
        props: (row: CurriculumPayload) => (
          <Stack gap={4}>
            <Text fw={600} fz={14} lineClamp={2}>
              {row.competencyName || '-'}
            </Text>
            <Group gap={4}>
              <Badge size='xs' variant='light' color='blue'>
                {row.competencyType || '-'}
              </Badge>
              <Badge size='xs' variant='light' color='grape'>
                Level {row.level || '-'}
              </Badge>
            </Group>
          </Stack>
        )
      },
      {
        heading: 'Competency Description',
        id: 'competencyDescription',
        sortable: false,
        active: true,
        props: (row: CurriculumPayload) => (
          <Text fz={13} c='dimmed'>
            {(row as any).competencyDescription || '-'}
          </Text>
        )
      },
      {
        heading: 'Category',
        id: 'category',
        sortable: true,
        active: true,
        props: (row: CurriculumPayload) => (
          <Tooltip
            label={row.categoryDescription || 'No description'}
            multiline
            w={300}
            withArrow
            disabled={!row.categoryDescription}
          >
            <Text fz={13} style={{ cursor: row.categoryDescription ? 'help' : 'default' }}>
              {row.category || '-'}
            </Text>
          </Tooltip>
        )
      },
      {
        heading: 'Job Positions',
        id: 'jobPositions',
        sortable: false,
        active: true,
        props: (row: CurriculumPayload) => {
          const positions = row.jobPositions || [];
          if (!Array.isArray(positions) || positions.length === 0) return '-';

          const positionNames = positions.map((pos: any) =>
            typeof pos === 'object' && pos?.name ? pos.name : pos
          );

          if (positionNames.length === 1) {
            return <Text fz={13}>{positionNames[0]}</Text>;
          }

          return (
            <Tooltip
              label={
                <Stack gap={4}>
                  {positionNames.map((name: string, idx: number) => (
                    <Text key={idx} fz={12} c='black'>
                      {idx + 1}. {name}
                    </Text>
                  ))}
                </Stack>
              }
              multiline
              w={280}
              withArrow
              color='white'
              styles={{ tooltip: { backgroundColor: 'white', color: 'black' } }}
            >
              <Group gap={4} style={{ cursor: 'help' }}>
                <Text fz={13}>{positionNames[0]}</Text>
                <Badge size='xs' variant='filled' color='gray'>
                  +{positionNames.length - 1}
                </Badge>
              </Group>
            </Tooltip>
          );
        }
      },
      {
        heading: 'Courses',
        id: 'courses',
        sortable: false,
        active: true,
        props: (row: CurriculumPayload) => {
          const courses = row.courses || [];
          if (courses.length === 0) return '-';

          if (courses.length === 1) {
            const course = courses[0];
            return (
              <Anchor
                href={`/app/lcms/courses/${course.courseId}`}
                target='_blank'
                rel='noopener noreferrer'
                size='sm'
                lineClamp={2}
              >
                {course.courseName}
              </Anchor>
            );
          }

          return (
            <Tooltip
              label={
                <Stack gap={4}>
                  {courses.map(
                    (
                      course: { courseId: string; courseName: string; positions: string[] },
                      idx: number
                    ) => (
                      <Anchor
                        key={course.courseId}
                        href={`/app/lcms/courses/${course.courseId}`}
                        target='_blank'
                        rel='noopener noreferrer'
                        fz={12}
                        c='black'
                        td='underline'
                      >
                        {idx + 1}. {course.courseName}
                      </Anchor>
                    )
                  )}
                </Stack>
              }
              multiline
              w={320}
              withArrow
              color='white'
              styles={{ tooltip: { backgroundColor: 'white', color: 'black' } }}
              events={{ hover: true, focus: true, touch: true }}
              closeDelay={200}
            >
              <Group gap={4} style={{ cursor: 'help' }}>
                <Anchor
                  href={`/app/lcms/courses/${courses[0].courseId}`}
                  target='_blank'
                  rel='noopener noreferrer'
                  size='sm'
                  lineClamp={1}
                >
                  {courses[0].courseName}
                </Anchor>
                <Badge size='xs' variant='filled' color='teal'>
                  +{courses.length - 1}
                </Badge>
              </Group>
            </Tooltip>
          );
        }
      },
      {
        heading: 'Details',
        id: 'details',
        sortable: false,
        active: true,
        props: (row: CurriculumPayload) => (
          <Stack gap={2}>
            <Group gap={4}>
              <Text fz={11} c='dimmed' fw={600}>
                BU:
              </Text>
              <Text fz={12} lineClamp={1}>
                {row.businessUnitName || row.businessUnitId || '-'}
              </Text>
            </Group>
            <Group gap={4}>
              <Text fz={11} c='dimmed' fw={600}>
                Job Band:
              </Text>
              <Text fz={12}>{row.managementLevel || '-'}</Text>
            </Group>
            {row.schoolName && row.schoolName !== '-' && (
              <Group gap={4}>
                <Text fz={11} c='dimmed' fw={600}>
                  School:
                </Text>
                <Text fz={12} lineClamp={1}>
                  {row.schoolName}
                </Text>
              </Group>
            )}
          </Stack>
        )
      }
    ],
    []
  );

  const versionCurriculumColumns = React.useMemo(
    () => [
      {
        heading: 'Competency',
        id: 'competencyName',
        sortable: true,
        active: true,
        props: (row: any) => (
          <Stack gap={4}>
            <Text fw={600} fz={14} lineClamp={2}>
              {row.competencyName || '-'}
            </Text>
            <Group gap={4}>
              <Badge size='xs' variant='light' color='grape'>
                Level {row.level || '-'}
              </Badge>
              {row.category && (
                <Badge size='xs' variant='light' color='blue'>
                  {row.category}
                </Badge>
              )}
            </Group>
          </Stack>
        )
      },
      {
        heading: 'Competency Description',
        id: 'competencyDescription',
        sortable: false,
        active: true,
        props: (row: any) => (
          <Text fz={13} c='dimmed'>
            {row.competencyDescription || '-'}
          </Text>
        )
      },
      {
        heading: 'Job Positions',
        id: 'jobPositionId',
        sortable: false,
        active: true,
        props: (row: any) => {
          if (row.jobPositions && Array.isArray(row.jobPositions)) {
            if (row.jobPositions.length > 0) {
              const positionNames = row.jobPositions.map((pos: any) =>
                typeof pos === 'object' && pos?.name ? pos.name : pos
              );

              if (positionNames.length === 1) {
                return <Text fz={13}>{positionNames[0]}</Text>;
              }

              return (
                <Tooltip
                  label={
                    <Stack gap={4}>
                      {positionNames.map((name: string, idx: number) => (
                        <Text key={idx} fz={12} c='black'>
                          {idx + 1}. {name}
                        </Text>
                      ))}
                    </Stack>
                  }
                  multiline
                  w={280}
                  withArrow
                  color='white'
                  styles={{ tooltip: { backgroundColor: 'white', color: 'black' } }}
                >
                  <Group gap={4} style={{ cursor: 'help' }}>
                    <Text fz={13}>{positionNames[0]}</Text>
                    <Badge size='xs' variant='filled' color='gray'>
                      +{positionNames.length - 1}
                    </Badge>
                  </Group>
                </Tooltip>
              );
            }
          }
          return <Text fz={13}>{row.jobPositionId || '-'}</Text>;
        }
      },
      {
        heading: 'Courses',
        id: 'courses',
        sortable: false,
        active: true,
        props: (row: any) => {
          const courses = row.courses || [];
          if (courses.length === 0) return <Text fz={13}>-</Text>;

          if (courses.length === 1) {
            const course = courses[0];
            const positions = course.positions || [];
            return (
              <Stack gap={4}>
                <Text fw={600} fz={13} lineClamp={2}>
                  {course.courseName || '-'}
                </Text>
                {positions.length > 0 && (
                  <Text fz={11} c='dimmed' lineClamp={1}>
                    {positions.join(', ')}
                  </Text>
                )}
                <Group gap={4} wrap='wrap'>
                  {course.learningHours && (
                    <Badge size='xs' variant='light' color='blue'>
                      {course.learningHours}h
                    </Badge>
                  )}
                  {course.modeOfDelivery && (
                    <Badge size='xs' variant='light' color='teal'>
                      {course.modeOfDelivery}
                    </Badge>
                  )}
                  {course.flaggingLIA && (
                    <Badge size='xs' variant='filled' color='orange'>
                      LIA
                    </Badge>
                  )}
                  {course.flaggingLIM && (
                    <Badge size='xs' variant='filled' color='pink'>
                      LIM
                    </Badge>
                  )}
                  {course.flaggingC && (
                    <Badge size='xs' variant='light' color='violet'>
                      {course.flaggingC}
                    </Badge>
                  )}
                </Group>
              </Stack>
            );
          }

          return (
            <Tooltip
              label={
                <Stack gap={8}>
                  {courses.map((course: any, idx: number) => {
                    const positions = course.positions || [];
                    return (
                      <Box key={course.courseId || idx}>
                        <Text fz={12} fw={600} c='black'>
                          {idx + 1}. {course.courseName || '-'}
                        </Text>
                        {positions.length > 0 && (
                          <Text fz={11} c='dimmed' mt={2}>
                            Positions: {positions.join(', ')}
                          </Text>
                        )}
                        <Group gap={4} mt={2} wrap='wrap'>
                          {course.learningHours && (
                            <Badge size='xs' variant='light' color='blue'>
                              {course.learningHours}h
                            </Badge>
                          )}
                          {course.modeOfDelivery && (
                            <Badge size='xs' variant='light' color='teal'>
                              {course.modeOfDelivery}
                            </Badge>
                          )}
                          {course.flaggingLIA && (
                            <Badge size='xs' variant='filled' color='orange'>
                              LIA
                            </Badge>
                          )}
                          {course.flaggingLIM && (
                            <Badge size='xs' variant='filled' color='pink'>
                              LIM
                            </Badge>
                          )}
                          {course.flaggingC && (
                            <Badge size='xs' variant='light' color='violet'>
                              {course.flaggingC}
                            </Badge>
                          )}
                        </Group>
                      </Box>
                    );
                  })}
                </Stack>
              }
              multiline
              w={400}
              withArrow
              color='white'
              styles={{ tooltip: { backgroundColor: 'white', color: 'black' } }}
            >
              <Group gap={4} style={{ cursor: 'help' }}>
                <Text fz={13} fw={600}>
                  {courses[0].courseName || '-'}
                </Text>
                <Badge size='xs' variant='filled' color='grape'>
                  +{courses.length - 1} more
                </Badge>
              </Group>
            </Tooltip>
          );
        }
      },
      {
        heading: 'Details',
        id: 'details',
        sortable: false,
        active: true,
        props: (row: any) => (
          <Stack gap={2}>
            <Group gap={4}>
              <Text fz={11} c='dimmed' fw={600}>
                BU:
              </Text>
              <Text fz={12}>{row.businessUnitId || '-'}</Text>
            </Group>
            <Group gap={4}>
              <Text fz={11} c='dimmed' fw={600}>
                Job Band:
              </Text>
              <Text fz={12}>{row.jobBand || '-'}</Text>
            </Group>
            {row.description && (
              <Tooltip label={row.description} multiline w={300} withArrow>
                <Text fz={12} c='dimmed' lineClamp={1} style={{ cursor: 'help' }}>
                  {row.description}
                </Text>
              </Tooltip>
            )}
          </Stack>
        )
      }
    ],
    []
  );

  /**
   * Transform versions to group rejected versions under approved versions.
   *
   * Example: If we have versions 1, 2, 3, 4, 5, 6, 7 with statuses:
   * - v1: rejected
   * - v2: rejected
   * - v3: approved
   * - v4: rejected
   * - v5: rejected
   * - v6: rejected
   * - v7: approved
   *
   * The display will be:
   * - v1 (approved, actual v3) with sub-versions 1.1, 1.2 (actual v1, v2)
   * - v2 (approved, actual v7) with sub-versions 2.1, 2.2, 2.3 (actual v4, v5, v6)
   */
  const transformVersions = (versions: MasterPlanVersion[]) => {
    // Sort versions by version number ascending (oldest to newest)
    const sortedVersions = [...versions].sort(
      (a, b) => (a.versionNumber || 0) - (b.versionNumber || 0)
    );

    const transformedVersions: any[] = [];
    let approvedVersionCount = 0;
    let pendingRejectedVersions: any[] = [];

    sortedVersions.forEach((version) => {
      const isApproved = version.status?.toLowerCase() === 'approved';

      if (isApproved) {
        approvedVersionCount++;

        // Create the main approved version with display version number
        const approvedVersion = {
          ...version,
          displayVersion: approvedVersionCount,
          originalVersion: version.versionNumber,
          rejectedSubVersions: [...pendingRejectedVersions],
          isApproved: true
        };

        transformedVersions.push(approvedVersion);

        // Clear pending rejected versions
        pendingRejectedVersions = [];
      } else {
        // This is a rejected version
        const subVersionNumber = pendingRejectedVersions.length + 1;
        const rejectedVersion = {
          ...version,
          displayVersion: `${approvedVersionCount || 0}.${subVersionNumber}`,
          originalVersion: version.versionNumber,
          isApproved: false,
          parentApprovedVersion: approvedVersionCount || 0
        };

        pendingRejectedVersions.push(rejectedVersion);
      }
    });

    // If there are remaining rejected versions (no approved version after them yet)
    // Attach them to the last approved version or create a placeholder
    if (pendingRejectedVersions.length > 0) {
      if (transformedVersions.length > 0) {
        // Add to the last approved version
        const lastApproved = transformedVersions[transformedVersions.length - 1];
        lastApproved.rejectedSubVersions = [
          ...lastApproved.rejectedSubVersions,
          ...pendingRejectedVersions
        ];
      } else {
        // No approved versions yet, just add the rejected versions as is
        transformedVersions.push(...pendingRejectedVersions);
      }
    }

    // Flatten the array to include all versions (approved + their rejected sub-versions)
    const flattenedVersions: any[] = [];
    transformedVersions.forEach((version) => {
      if (version.isApproved) {
        // Add rejected sub-versions first (they came before the approval)
        if (version.rejectedSubVersions && version.rejectedSubVersions.length > 0) {
          flattenedVersions.push(...version.rejectedSubVersions);
        }
        // Then add the approved version
        flattenedVersions.push(version);
      } else {
        flattenedVersions.push(version);
      }
    });

    return {
      allVersions: flattenedVersions.reverse(), // Reverse to show newest first
      approvedVersionCount,
      transformedVersions: transformedVersions.reverse() // For accordion display
    };
  };

  // Prepare grouped versions data for accordion view
  const groupedVersionsData = React.useMemo(() => {
    const grouped: any[] = [];
    jobFamilyList.forEach((jobFamily) => {
      jobFamily.masterplans?.forEach((masterplan) => {
        if (masterplan.versions && masterplan.versions.length > 0) {
          const { allVersions, approvedVersionCount } = transformVersions(masterplan.versions);

          // Get the latest version (could be approved or rejected)
          const latestVersion = allVersions[0];

          grouped.push({
            jobFamily: jobFamily.jobFamily,
            jobBand: masterplan.jobBand,
            businessUnit: masterplan.businessUnitName || masterplan.businessUnitId,
            masterplanId: masterplan.msDACUMProgramID,
            latestVersion: latestVersion,
            allVersions: allVersions,
            approvedVersionCount: approvedVersionCount,
            totalVersions: allVersions.length
          });
        }
      });
    });
    return grouped;
  }, [jobFamilyList]);

  // Helper function to check if a value is different from the previous version
  // This highlights the first point of change between versions
  const shouldHighlight = (
    fieldKey: string,
    currentValue: any,
    allVersions: any[],
    currentIndex: number
  ) => {
    // Don't highlight the first version (nothing to compare to)
    if (currentIndex === 0) return false;

    // Don't highlight empty or missing values
    if (!currentValue || currentValue === '-' || currentValue === '') return false;

    // Get the previous version's value
    const previousVersion = allVersions[currentIndex - 1];
    if (!previousVersion) return false;

    const previousValue = previousVersion[fieldKey];

    // Highlight if the current value is different from the previous version
    return currentValue !== previousValue;
  };

  // Helper function to get highlight background color
  const getHighlightStyle = (shouldHighlight: boolean) => {
    if (!shouldHighlight) return {};

    return {
      backgroundColor: '#fff3cd',
      border: '1px solid #ffeaa7',
      borderRadius: '4px',
      padding: '4px 8px',
      fontWeight: 600
    };
  };

  // Helper function to strip HTML tags from text
  const stripHtmlTags = (html: string | null | undefined): string => {
    if (!html) return '-';

    // Create a temporary div element to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    const text = tempDiv.textContent || tempDiv.innerText || '';

    // eslint-disable-next-line no-control-regex
    const cleaned = text.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '');

    return cleaned.replace(/\s+/g, ' ').trim() || '-';
  };

  const getCompetencyType = (curriculum: any) =>
    curriculum?.competencyType ||
    curriculum?.competencytype ||
    curriculum?.competency_type ||
    curriculum?.competencyTypeName;

  const getJobBand = (curriculum: any) => curriculum?.jobBand || curriculum?.managementLevel;

  const CATEGORY_DESCRIPTION_MAP: Record<string, string> = {
    'fundamental thinking skills':
      'The ability to explain essential information and apply it accurately to perform tasks and solve routine problems.',
    'fundamental thinking skill':
      'The ability to explain essential information and apply it accurately to perform tasks and solve routine problems.',
    'higher order thinking':
      'The ability to analyze situations, make sound judgments, and create effective solutions in complex or unfamiliar contexts.',
    'high order thinking':
      'The ability to analyze situations, make sound judgments, and create effective solutions in complex or unfamiliar contexts.'
  };

  const getCategoryDescription = (
    category: string | undefined | null,
    fallback?: string | null
  ) => {
    const normalized = (category || '').trim().toLowerCase();
    return CATEGORY_DESCRIPTION_MAP[normalized] || fallback || '-';
  };

  const approveMasterPlanMutation = useMutation({
    mutationFn: () => masterPlanService.approveMasterPlan(currentMasterplanId),
    onSuccess: (response) => {
      showSuccessNotification({
        title: 'Master Plan Approved',
        message: response?.message || 'Master plan has been approved successfully.'
      });
      queryClient.invalidateQueries({ queryKey: ['masterPlanListByGroup'] });
      queryClient.invalidateQueries({
        queryKey: ['masterPlanDetailByGroup', jobFamilyGroup, year, endYear]
      });
    },
    onError: (mutationError: any) => {
      showErrorNotification({
        message:
          mutationError?.response?.data?.message ||
          mutationError?.message ||
          'Unable to approve master plan. Please try again.'
      });
    }
  });

  const rejectMasterPlanMutation = useMutation({
    mutationFn: () =>
      masterPlanService.rejectMasterPlan({
        msDACUMProgramID: currentMasterplanId,
        reason: rejectReason.trim()
      }),
    onSuccess: (response) => {
      showSuccessNotification({
        title: 'Master Plan Rejected',
        message: response?.message || 'Master plan has been rejected successfully.'
      });
      setRejectReason('');
      closeRejectModal();
      queryClient.invalidateQueries({ queryKey: ['masterPlanListByGroup'] });
      queryClient.invalidateQueries({
        queryKey: ['masterPlanDetailByGroup', jobFamilyGroup, year, endYear]
      });
    },
    onError: (mutationError: any) => {
      showErrorNotification({
        message:
          mutationError?.response?.data?.message ||
          mutationError?.message ||
          'Unable to reject master plan. Please try again.'
      });
    }
  });

  const deleteMasterPlanMutation = useMutation({
    mutationFn: () => masterPlanService.deleteMasterPlan(currentMasterplanId),
    onSuccess: (response) => {
      showSuccessNotification({
        title: 'Master Plan Deleted',
        message: response?.message || 'Master plan has been deleted successfully.'
      });
      queryClient.invalidateQueries({ queryKey: ['masterPlanListByGroup'] });
      navigate(pathnames.reviewMasterPlan);
    },
    onError: (mutationError: any) => {
      showErrorNotification({
        message:
          mutationError?.response?.data?.message ||
          mutationError?.message ||
          'Unable to delete master plan. Please try again.'
      });
    }
  });

  // Conditional returns
  if (!jobFamilyGroup || !year) {
    return <Alert color='red'>Job family group and year are required.</Alert>;
  }

  if (isPending) {
    return (
      <Flex gap='xl' direction={{ base: 'column', md: 'row' }}>
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Stack gap='xl'>
            {/* Action Buttons Skeleton */}
            <Group gap='sm' wrap='wrap'>
              <Skeleton height={36} width={140} radius='md' />
              <Skeleton height={36} width={140} radius='md' />
              <Skeleton height={36} width={120} radius='md' />
            </Group>

            {/* Header Skeleton */}
            <Paper p='xl' radius='lg' withBorder>
              <Stack gap='md'>
                <Group justify='space-between' align='flex-start'>
                  <Stack gap={4}>
                    <Skeleton height={32} width={300} />
                    <Skeleton height={20} width={200} />
                  </Stack>
                  <Skeleton height={32} width={100} radius='md' />
                </Group>
              </Stack>
            </Paper>

            {/* Overview Skeleton */}
            <Card radius='lg' withBorder>
              <Stack gap='lg'>
                <Skeleton height={24} width={120} />
                <Box
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                    gap: '16px'
                  }}
                >
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <Paper key={i} withBorder radius='md' p='md'>
                      <Skeleton height={16} width='60%' mb={8} />
                      <Skeleton height={20} width='80%' />
                    </Paper>
                  ))}
                </Box>
              </Stack>
            </Card>

            {/* Competency List Skeleton */}
            <Card radius='lg' withBorder>
              <Stack gap='md'>
                <Group justify='space-between'>
                  <Skeleton height={24} width={180} />
                  <Skeleton height={32} width={100} radius='md' />
                </Group>
                <Stack gap='sm'>
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} height={60} radius='md' />
                  ))}
                </Stack>
              </Stack>
            </Card>
          </Stack>
        </Box>

        {/* Sidebar Skeletons */}
        <Stack gap='md' style={{ width: isMobile ? '100%' : '320px', flexShrink: 0 }}>
          <Paper withBorder radius='lg' p='lg'>
            <Skeleton height={24} width='70%' mb='lg' />
            <Stack gap='sm'>
              {[1, 2].map((i) => (
                <Skeleton key={i} height={100} radius='lg' />
              ))}
            </Stack>
          </Paper>
          <Paper withBorder radius='lg' p='lg'>
            <Skeleton height={24} width='70%' mb='lg' />
            <Stack gap='sm'>
              {[1, 2].map((i) => (
                <Skeleton key={i} height={120} radius='lg' />
              ))}
            </Stack>
          </Paper>
        </Stack>
      </Flex>
    );
  }

  if (error) {
    return <Alert color='red'>Unable to load master plan detail. Please try again later.</Alert>;
  }

  if (!selectedJobFamily || !data) {
    return <Alert color='yellow'>Master plan detail is not available.</Alert>;
  }

  const canModify =
    !data.status && profile?.isManager === true && profile?.areaName === 'Learning Solutions';
  // const canModify = !data.status && profile?.isManager === true;
  const isApproved = data.status?.toLowerCase() === 'approved';
  const isRejected = data.status?.toLowerCase() === 'rejected';
  const hasVersions = data.versions && data.versions.length > 0;
  const isActionLoading =
    approveMasterPlanMutation.isPending ||
    rejectMasterPlanMutation.isPending ||
    deleteMasterPlanMutation.isPending;

  const statusLabel = data.status || 'Pending';
  const statusColorMap: Record<string, string> = {
    approved: 'teal',
    rejected: 'red',
    pending: 'yellow',
    draft: 'gray'
  };
  const statusColor = statusColorMap[statusLabel.toLowerCase()] || 'gray';

  const overviewItems = [
    { label: 'Job Family Group', value: jobFamilyGroup || '-' },
    { label: 'Job Family', value: selectedJobFamily.jobFamily || '-' },
    { label: 'Job Band', value: data.jobBand || '-' },
    { label: 'Business Unit', value: data.businessUnitName || data.businessUnitId || '-' },
    { label: 'Year', value: data.year?.toString() || '-' },
    { label: 'Curriculum Items', value: data.curriculumList.length.toString() }
  ];

  const handleApprove = () => {
    if (!canModify) return;
    approveMasterPlanMutation.mutate();
  };

  const handleRejectSubmit = () => {
    if (!canModify) return;
    if (!rejectReason.trim()) {
      showErrorNotification({ message: 'Please provide a rejection reason.' });
      return;
    }
    rejectMasterPlanMutation.mutate();
  };

  const handleRejectModalClose = () => {
    if (rejectMasterPlanMutation.isPending) {
      return;
    }
    setRejectReason('');
    closeRejectModal();
  };

  const handleExportToExcel = () => {
    if (!selectedJobFamily || !masterplanList || masterplanList.length === 0) return;

    try {
      const workbook = XLSX.utils.book_new();

      masterplanList.forEach((masterplan, index) => {
        if (!masterplan.curriculumList || masterplan.curriculumList.length === 0) {
          return;
        }

        const excelData = masterplan.curriculumList.flatMap((curriculum: any) => {
          if (!curriculum) return [];

          if (!curriculum.courses || curriculum.courses.length === 0) {
            return [
              {
                'Competency Type': getCompetencyType(curriculum) || '-',
                'Competency Name': curriculum.competencyName || '-',
                'Competency Description': curriculum.competencyDescription || '-',
                Category: curriculum.category || '-',
                'Category Description': getCategoryDescription(
                  curriculum.category,
                  curriculum.categoryDescription
                ),
                Level: curriculum.level?.toString() || '-',
                Module: '-',
                LO: '-',
                'Learning Hours': '-',
                'Mode of Delivery': '-',
                LIM: '-',
                LIA: '-',
                'Job Positions':
                  curriculum.jobPositions &&
                  Array.isArray(curriculum.jobPositions) &&
                  curriculum.jobPositions.length > 0
                    ? curriculum.jobPositions
                        .map((pos: any) =>
                          typeof pos === 'object' && pos?.name ? pos.name : String(pos)
                        )
                        .join(', ')
                    : '-',
                'Job Band': getJobBand(curriculum) || '-',
                'Job Fam': curriculum.jobFamilyName || '-',
                'Job Fam Group': jobFamilyGroup || '-',
                'Divisi / Unit': curriculum.businessUnitName || '-'
              }
            ];
          }

          return curriculum.courses.map((course: any) => ({
            'Competency Type': getCompetencyType(curriculum) || '-',
            'Competency Name': curriculum.competencyName || '-',
            'Competency Description': curriculum.competencyDescription || '-',
            Category: curriculum.category || '-',
            'Category Description': getCategoryDescription(
              curriculum.category,
              curriculum.categoryDescription
            ),
            Level: curriculum.level?.toString() || '-',
            Module: course?.courseName || '-',
            LO: stripHtmlTags(course?.learningObjectives),
            Content: '-',
            'Learning Hours': course?.learningHours != null ? String(course.learningHours) : '-',
            'Mode of Delivery': course?.modeOfDelivery || '-',
            LIM: course?.flaggingLIM ? 'Yes' : 'No',
            LIA: course?.flaggingLIA ? 'Yes' : 'No',
            'Job Positions':
              course?.positions && Array.isArray(course.positions) && course.positions.length > 0
                ? course.positions.join(', ')
                : curriculum.jobPositions &&
                    Array.isArray(curriculum.jobPositions) &&
                    curriculum.jobPositions.length > 0
                  ? curriculum.jobPositions
                      .map((pos: any) =>
                        typeof pos === 'object' && pos?.name ? pos.name : String(pos)
                      )
                      .join(', ')
                  : '-',
            'Job Band': getJobBand(curriculum) || '-',
            'Job Fam': curriculum.jobFamilyName || '-',
            'Job Fam Group': jobFamilyGroup || '-',
            'Divisi / Unit': curriculum.businessUnitName || '-'
          }));
        });

        if (excelData.length === 0) return;

        const worksheet = XLSX.utils.json_to_sheet(excelData);

        worksheet['!cols'] = [
          { wch: 20 }, // Competency Type
          { wch: 40 }, // Competency Name
          { wch: 50 }, // Competency Description
          { wch: 20 }, // Category
          { wch: 50 }, // Category Description
          { wch: 12 }, // Level
          { wch: 40 }, // Module
          { wch: 50 }, // LO
          { wch: 20 }, // Content
          { wch: 15 }, // Learning Hours
          { wch: 20 }, // Mode of Delivery
          { wch: 15 }, // LIM
          { wch: 12 }, // LIA
          { wch: 40 }, // Job Positions
          { wch: 15 }, // Job Band
          { wch: 30 }, // Job Fam
          { wch: 30 }, // Job Fam Group
          { wch: 30 } // Divisi / Unit
        ];

        const businessUnit = masterplan.businessUnitName || masterplan.businessUnitId || 'Unknown';
        const jobBand = masterplan.jobBand || 'Unknown';
        const baseName = `${businessUnit} - ${jobBand}`;
        let sheetName = `${index + 1}. ${baseName}`.substring(0, 31).replace(/[\\/?*[\]]/g, '');

        // Ensure unique sheet name to avoid XLSX throwing on duplicates
        let sheetCounter = 2;
        while (workbook.SheetNames.includes(sheetName)) {
          const suffix = ` (${sheetCounter})`;
          sheetName =
            `${index + 1}. ${baseName}`
              .substring(0, 31 - suffix.length)
              .replace(/[\\/?*[\]]/g, '') + suffix;
          sheetCounter++;
        }

        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
      });

      if (workbook.SheetNames.length === 0) {
        showErrorNotification({
          message: 'No data available to export.'
        });
        return;
      }

      const fileName = `MasterPlan_${selectedJobFamily.jobFamily.replace(/[\\/?*[\]]/g, '_')}_${year}_${new Date().toISOString().split('T')[0]}.xlsx`;

      XLSX.writeFile(workbook, fileName);

      showSuccessNotification({
        title: 'Export Successful',
        message: 'Master plan has been exported to Excel successfully.'
      });
    } catch {
      showErrorNotification({
        message: 'Failed to export master plan. Please try again.'
      });
    }
  };

  const handleExportVersionHistory = (group: any) => {
    if (!group || !group.allVersions || group.allVersions.length === 0) {
      showErrorNotification({
        message: 'No version history available to export.'
      });
      return;
    }

    try {
      const workbook = XLSX.utils.book_new();
      const allVersions = group.allVersions || [];

      // Create a sheet for each version
      allVersions.forEach((version: any) => {
        const curriculum = version.curriculum || [];

        if (curriculum.length === 0) {
          // Still create a sheet but with version info only
          const versionInfo = [
            {
              Version: `v${version.displayVersion || version.versionNumber}`,
              'Original Version': version.originalVersion ? `v${version.originalVersion}` : '-',
              'Program Name': version.programName || '-',
              Status: version.status || 'Pending',
              'Job Family': version.jobFamily || '-',
              'Job Band': version.jobBand || '-',
              'Business Unit': version.businessUnitName || version.businessUnitId || '-',
              'Created At': version.createdAt || '-',
              Note: version.note || '-',
              Description: version.description || '-',
              'Curriculum Count': '0'
            }
          ];
          const worksheet = XLSX.utils.json_to_sheet(versionInfo);
          const sheetName = `v${version.displayVersion || version.versionNumber}`.substring(0, 31);
          XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
          return;
        }

        const excelData = curriculum.flatMap((curr: any) => {
          if (!curr) return [];

          const courses = curr.courses || [];

          if (courses.length === 0) {
            return [
              {
                Version: `v${version.displayVersion || version.versionNumber}`,
                'Original Version': version.originalVersion ? `v${version.originalVersion}` : '-',
                'Competency Type': getCompetencyType(curr) || '-',
                'Competency Name': curr.competencyName || '-',
                'Competency Description': curr.competencyDescription || '-',
                Category: curr.category || '-',
                'Category Description': getCategoryDescription(
                  curr.category,
                  curr.categoryDescription
                ),
                Level: curr.level?.toString() || '-',
                Module: '-',
                LO: '-',
                Content: '-',
                'Learning Hours': '-',
                'Mode of Delivery': '-',
                LIM: '-',
                LIA: '-',
                'Job Positions':
                  curr.jobPositions &&
                  Array.isArray(curr.jobPositions) &&
                  curr.jobPositions.length > 0
                    ? curr.jobPositions
                        .map((pos: any) =>
                          typeof pos === 'object' && pos?.name ? pos.name : String(pos)
                        )
                        .join(', ')
                    : '-',
                'Job Band': getJobBand(curr) || version.jobBand || '-',
                'Job Fam': version.jobFamily || '-',
                'Job Fam Group': jobFamilyGroup || '-',
                'Business Unit': version.businessUnitName || version.businessUnitId || '-'
              }
            ];
          }

          return courses.map((course: any) => ({
            Version: `v${version.displayVersion || version.versionNumber}`,
            'Original Version': version.originalVersion ? `v${version.originalVersion}` : '-',
            'Competency Type': getCompetencyType(curr) || '-',
            'Competency Name': curr.competencyName || '-',
            'Competency Description': curr.competencyDescription || '-',
            Category: curr.category || '-',
            'Category Description': getCategoryDescription(curr.category, curr.categoryDescription),
            Level: curr.level?.toString() || '-',
            Module: course?.courseName || '-',
            LO: stripHtmlTags(course?.learningObjectives),
            Content: '-',
            'Learning Hours': course?.learningHours != null ? String(course.learningHours) : '-',
            'Mode of Delivery': course?.modeOfDelivery || '-',
            LIM: course?.flaggingC ? (course?.flaggingLIM ? 'Yes' : 'No') : '-',
            LIA: course?.flaggingLIA ? 'Yes' : 'No',
            'Job Positions':
              course?.positions && Array.isArray(course.positions) && course.positions.length > 0
                ? course.positions.join(', ')
                : curr.jobPositions &&
                    Array.isArray(curr.jobPositions) &&
                    curr.jobPositions.length > 0
                  ? curr.jobPositions
                      .map((pos: any) =>
                        typeof pos === 'object' && pos?.name ? pos.name : String(pos)
                      )
                      .join(', ')
                  : '-',
            'Job Band': getJobBand(curr) || version.jobBand || '-',
            'Job Fam': version.jobFamily || '-',
            'Job Fam Group': jobFamilyGroup || '-',
            'Business Unit': version.businessUnitName || version.businessUnitId || '-'
          }));
        });

        if (excelData.length === 0) return;

        const worksheet = XLSX.utils.json_to_sheet(excelData);

        worksheet['!cols'] = [
          { wch: 12 }, // Version
          { wch: 18 }, // Original Version
          { wch: 20 }, // Competency Type
          { wch: 40 }, // Competency Name
          { wch: 50 }, // Competency Description
          { wch: 20 }, // Category
          { wch: 50 }, // Category Description
          { wch: 12 }, // Level
          { wch: 40 }, // Module
          { wch: 50 }, // LO
          { wch: 20 }, // Content
          { wch: 15 }, // Learning Hours
          { wch: 20 }, // Mode of Delivery
          { wch: 15 }, // LIM
          { wch: 12 }, // LIA
          { wch: 40 }, // Job Positions
          { wch: 15 }, // Job Band
          { wch: 30 }, // Job Fam
          { wch: 30 }, // Job Fam Group
          { wch: 30 } // Business Unit
        ];

        const sheetName = `v${version.displayVersion || version.versionNumber}`.substring(0, 31);
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
      });

      if (workbook.SheetNames.length === 0) {
        showErrorNotification({
          message: 'No data available to export.'
        });
        return;
      }

      const jobBand = group.jobBand?.replace(/[\\/?*[\]]/g, '_') || 'Unknown';
      const businessUnit = group.businessUnit?.replace(/[\\/?*[\]]/g, '_') || 'Unknown';
      const fileName = `VersionHistory_${jobBand}_${businessUnit}_${year}_${new Date().toISOString().split('T')[0]}.xlsx`;

      XLSX.writeFile(workbook, fileName);

      showSuccessNotification({
        title: 'Export Successful',
        message: 'Version history has been exported to Excel successfully.'
      });
    } catch (error) {
      showErrorNotification({
        message:
          'Failed to export version history. Please try again. (' +
          (error instanceof Error ? error.message : String(error)) +
          ')'
      });
    }
  };

  return (
    <Flex gap='xl' direction={{ base: 'column', md: 'row' }}>
      {/* Main Content */}
      <Box style={{ flex: 1, minWidth: 0 }}>
        <Stack gap='xl'>
          {/* Breadcrumb Navigation */}
          <Paper withBorder p='md' radius='md' bg='gray.0'>
            <Group gap='xs'>
              <Text fz={14} fw={500} c='dimmed'>
                Job Family Group:
              </Text>
              <Text fz={14} fw={700} c='blue'>
                {jobFamilyGroup}
              </Text>
              <IconChevronDown size={16} style={{ transform: 'rotate(-90deg)' }} />
              <Text fz={14} fw={500} c='dimmed'>
                Job Family:
              </Text>
              <Text fz={14} fw={700} c='blue'>
                {selectedJobFamily.jobFamily}
              </Text>
            </Group>
          </Paper>

          {/* Action Buttons - Redesigned */}
          <Paper withBorder radius='md' p='md' bg='gray.0'>
            <Stack gap='xs'>
              <Group gap='xs'>
                <Text fz={12} c='dimmed' fw={500}>
                  Logged in as:
                </Text>
                <Badge
                  size='sm'
                  variant='light'
                  color={profile?.isManager ? 'teal' : 'gray'}
                  radius='sm'
                >
                  {profile?.isManager ? 'Manager' : 'Non-Manager'}
                </Badge>
                {profile?.areaName && (
                  <Badge size='sm' variant='light' color='blue' radius='sm'>
                    {profile.areaName}
                  </Badge>
                )}
              </Group>
            </Stack>
            <Group justify='space-between' wrap='wrap' gap='md' mt='sm'>
              <Group gap='xs'>
                <CopyButton
                  value={`${window.location.origin}/app/dacum/masterplan/shared/${data.msDACUMProgramID}?jobfamily=${encodeURIComponent(selectedJobFamily.jobFamily)}&year=${year}`}
                >
                  {({ copied, copy }) => (
                    <Tooltip label={copied ? 'Link copied!' : 'Copy share link'} withArrow>
                      <ActionIcon
                        variant='light'
                        size='lg'
                        color={copied ? 'teal' : 'gray'}
                        onClick={copy}
                        radius='md'
                      >
                        {copied ? <IconCheck size={18} /> : <IconCopy size={18} />}
                      </ActionIcon>
                    </Tooltip>
                  )}
                </CopyButton>

                <Tooltip label='View all versions' withArrow>
                  <ActionIcon
                    variant='light'
                    size='lg'
                    color='violet'
                    onClick={openVersionOverview}
                    disabled={isActionLoading}
                    radius='md'
                  >
                    <IconEye size={18} />
                  </ActionIcon>
                </Tooltip>

                <Tooltip label='Export to Excel' withArrow>
                  <ActionIcon
                    variant='light'
                    size='lg'
                    color='green'
                    onClick={handleExportToExcel}
                    disabled={isActionLoading}
                    radius='md'
                  >
                    <IconDownload size={18} />
                  </ActionIcon>
                </Tooltip>
              </Group>

              <Group gap='xs'>
                <Button
                  variant='default'
                  size='sm'
                  leftSection={<IconEdit size={16} />}
                  onClick={() => {
                    const editUrl = pathnames.editMasterPlan.replace(':id', data.msDACUMProgramID);
                    const queryParams = new URLSearchParams();
                    queryParams.append('jobfamily', selectedJobFamily.jobFamily);
                    queryParams.append('jobFamilyGroup', jobFamilyGroup);
                    queryParams.append('year', (data.year || new Date().getFullYear()).toString());
                    if (endYear) {
                      queryParams.append('endYear', endYear);
                    }
                    queryParams.append('masterplanId', data.msDACUMProgramID);
                    navigate(`${editUrl}?${queryParams.toString()}`);
                  }}
                  disabled={isActionLoading}
                  radius='md'
                >
                  {isApproved || isRejected ? 'Revise' : 'Edit'}
                </Button>
                {canModify && (
                  <>
                    <Button
                      variant='light'
                      color='teal'
                      size='sm'
                      leftSection={<IconThumbUp size={16} />}
                      onClick={handleApprove}
                      loading={approveMasterPlanMutation.isPending}
                      radius='md'
                    >
                      Approve
                    </Button>
                    <Button
                      variant='light'
                      color='red'
                      size='sm'
                      leftSection={<IconThumbDown size={16} />}
                      onClick={openRejectModal}
                      loading={rejectMasterPlanMutation.isPending}
                      radius='md'
                    >
                      Reject
                    </Button>
                  </>
                )}
              </Group>
            </Group>
          </Paper>

          {/* Modern Header */}
          <Paper
            p='xl'
            radius='lg'
            withBorder
            style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
          >
            <Stack gap='md'>
              <Group justify='space-between' align='flex-start'>
                <Stack gap={4}>
                  <Text fw={700} fz={28} c='white'>
                    {data.programName}
                  </Text>
                  <Text fz={15} c='white' opacity={0.9}>
                    {selectedJobFamily.jobFamily} • {data.jobBand || 'N/A'}
                  </Text>
                </Stack>
                <Badge color={statusColor} variant='filled' size='lg' radius='md'>
                  {statusLabel}
                </Badge>
              </Group>
            </Stack>
          </Paper>

          <Card radius='lg' withBorder>
            <Stack gap='lg'>
              <Text fw={700} fz={18} c='blue'>
                Overview
              </Text>

              <Box
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                  gap: '16px'
                }}
              >
                {overviewItems.map((item) => (
                  <Paper key={item.label} withBorder radius='md' p='md'>
                    <Text fz={12} fw={600} c='dimmed' tt='uppercase'>
                      {item.label}
                    </Text>
                    <Text fw={600}>{item.value}</Text>
                  </Paper>
                ))}
              </Box>

              <Box
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                  gap: '16px'
                }}
              >
                <Paper withBorder radius='md' p='md'>
                  <Stack gap={6}>
                    <Text fw={600}>Description</Text>
                    <Text fz={14}>{data.description || '-'}</Text>
                  </Stack>
                </Paper>
              </Box>
            </Stack>
          </Card>

          <Card radius='lg' withBorder>
            <Stack gap='md'>
              <Group justify='space-between' align='center'>
                <Text fw={700} fz={18} c='blue'>
                  Competency List
                </Text>
                <Badge color='grape' variant='light' size='lg' radius='md'>
                  {data.curriculumList.length} item{data.curriculumList.length === 1 ? '' : 's'}
                </Badge>
              </Group>
              <DataTables
                columns={curriculumColumns}
                data={data.curriculumList}
                loading={false}
                withPagination={false}
                emptyMessageTitle='No curriculum data'
                emptyMessageDescription='Curriculum entries will appear after they are linked to this master plan.'
              />
            </Stack>
          </Card>

          {/* Job Position Competency Overview */}
          {jobPositionCompetencyCount.length > 0 && (
            <Card withBorder shadow='sm' radius='md' p='md'>
              <Stack gap='sm'>
                <div>
                  <Group justify='space-between' align='center'>
                    <div>
                      <Text fw={600} size='md' c='dark'>
                        Competency Overview
                      </Text>
                      <Text size='xs' c='dimmed' mt={2}>
                        {jobPositionCompetencyCount.length} position
                        {jobPositionCompetencyCount.length > 1 ? 's' : ''}
                      </Text>
                    </div>
                  </Group>
                  <Divider my='sm' />
                </div>

                <Accordion
                  variant='contained'
                  radius='sm'
                  chevronPosition='right'
                  styles={{
                    item: {
                      border: '1px solid #e9ecef',
                      marginBottom: '6px',
                      backgroundColor: '#fff'
                    },
                    control: {
                      padding: '8px 12px'
                    },
                    content: {
                      padding: '0'
                    }
                  }}
                >
                  {jobPositionCompetencyCount.map((position) => (
                    <Accordion.Item
                      key={position.id}
                      value={position.id}
                      style={{ borderLeft: '3px solid #339af0' }}
                    >
                      <Accordion.Control>
                        <Group justify='space-between' wrap='nowrap' pr='sm'>
                          <Text fw={500} size='sm' lineClamp={1} style={{ flex: 1 }}>
                            {position.name}
                          </Text>
                          <Badge size='sm' color='blue' variant='filled'>
                            {position.count} competenc{position.count === 1 ? 'y' : 'ies'}
                          </Badge>
                        </Group>
                      </Accordion.Control>
                      <Accordion.Panel>
                        <Stack gap={4} p={4}>
                          {position.competencies.map((competency, idx) => (
                            <Group
                              key={idx}
                              gap='xs'
                              wrap='nowrap'
                              p={4}
                              style={{
                                backgroundColor: idx % 2 === 0 ? '#f0f7ff' : '#fff',
                                borderRadius: '3px'
                              }}
                            >
                              <Badge
                                color='blue'
                                size='xs'
                                circle
                                variant='filled'
                                style={{ minWidth: '18px' }}
                              >
                                {idx + 1}
                              </Badge>
                              <Text size='xs' style={{ flex: 1 }}>
                                {competency}
                              </Text>
                            </Group>
                          ))}
                        </Stack>
                      </Accordion.Panel>
                    </Accordion.Item>
                  ))}
                </Accordion>
              </Stack>
            </Card>
          )}

          {hasVersions &&
            (() => {
              const { allVersions } = transformVersions(data.versions);

              return (
                <Card radius='lg' withBorder>
                  <Stack gap='md'>
                    <Group justify='space-between' align='center'>
                      <Group gap='md' align='center'>
                        <Box
                          style={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            borderRadius: '8px',
                            padding: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <IconHistory size={20} color='white' />
                        </Box>
                        <Text fw={700} fz={18} c='blue'>
                          Version History
                        </Text>
                        <Badge color='blue' variant='light' size='lg' radius='md'>
                          {allVersions.length} version{allVersions.length === 1 ? '' : 's'}
                        </Badge>
                      </Group>
                      <Text fz={13} c='dimmed'>
                        Click to view curriculum details for each version
                      </Text>
                    </Group>

                    <Accordion variant='separated' radius='md' chevronPosition='right'>
                      {allVersions.map((version, versionIndex) => (
                        <Accordion.Item
                          key={version.originalVersion || version.versionNumber}
                          value={`version-${version.originalVersion || version.versionNumber}`}
                          style={{ border: '2px solid #e9ecef', borderRadius: '8px' }}
                        >
                          <Accordion.Control>
                            <Group justify='space-between' wrap='nowrap' pr='md'>
                              <Group gap='md'>
                                <Box>
                                  <Badge
                                    size='xl'
                                    radius='md'
                                    variant='gradient'
                                    gradient={{ from: 'blue', to: 'grape' }}
                                    color={version.isApproved === false ? 'red' : undefined}
                                  >
                                    v{version.displayVersion || version.versionNumber}
                                  </Badge>
                                  {versionIndex === 0 && (
                                    <Text fz={9} c='blue' ta='center' fw={600} mt={2}>
                                      LATEST
                                    </Text>
                                  )}
                                  {version.isApproved === false && (
                                    <Text fz={9} c='red' ta='center' fw={600} mt={2}>
                                      REJECTED
                                    </Text>
                                  )}
                                </Box>
                                <Stack gap={4}>
                                  <Text fw={600} fz={15}>
                                    {version.programName}
                                  </Text>
                                  <Group gap='xs'>
                                    {version.status && (
                                      <Badge
                                        color={
                                          statusColorMap[version.status.toLowerCase()] || 'gray'
                                        }
                                        variant='filled'
                                        size='sm'
                                        radius='md'
                                      >
                                        {version.status}
                                      </Badge>
                                    )}
                                    <Badge size='sm' variant='light' color='grape'>
                                      {version.curriculum?.length || 0} item
                                      {version.curriculum?.length === 1 ? '' : 's'}
                                    </Badge>
                                  </Group>
                                  <Text fz={12} c='dimmed'>
                                    <Dates date={version.createdAt} dateFormat='dateNameTime' />
                                  </Text>
                                </Stack>
                              </Group>
                            </Group>
                          </Accordion.Control>
                          <Accordion.Panel>
                            <Stack gap='md'>
                              {version.note && (
                                <Paper withBorder radius='md' p='md' bg='yellow.0'>
                                  <Group gap={4} mb={4}>
                                    <Text fz={12} fw={700} c='yellow.9' tt='uppercase'>
                                      Note
                                    </Text>
                                  </Group>
                                  <Text fz={14} fw={500}>
                                    {version.note}
                                  </Text>
                                </Paper>
                              )}

                              {version.description && (
                                <Paper withBorder radius='md' p='md' bg='blue.0'>
                                  <Group gap={4} mb={4}>
                                    <Text fz={12} fw={700} c='blue.7' tt='uppercase'>
                                      Description
                                    </Text>
                                  </Group>
                                  <Text fz={14} fw={500}>
                                    {version.description}
                                  </Text>
                                </Paper>
                              )}

                              {version.curriculum && version.curriculum.length > 0 ? (
                                <Box>
                                  <Group mb='md' align='center'>
                                    <Text fw={700} fz={16} c='grape.8'>
                                      Curriculum / Competency
                                    </Text>
                                    <Badge color='grape' variant='light' radius='md'>
                                      {version.curriculum.length} items
                                    </Badge>
                                  </Group>
                                  <DataTables
                                    columns={versionCurriculumColumns}
                                    data={version.curriculum}
                                    loading={false}
                                    withPagination={false}
                                    emptyMessageTitle='No curriculum'
                                    emptyMessageDescription='No curriculum entries for this version.'
                                  />
                                </Box>
                              ) : (
                                <Paper withBorder radius='md' p='md' bg='gray.0'>
                                  <Text fz={14} c='dimmed' ta='center'>
                                    No curriculum data available for this version
                                  </Text>
                                </Paper>
                              )}
                            </Stack>
                          </Accordion.Panel>
                        </Accordion.Item>
                      ))}
                    </Accordion>
                  </Stack>
                </Card>
              );
            })()}

          <Modal
            opened={rejectOpened}
            onClose={handleRejectModalClose}
            title='Reject Master Plan'
            centered
          >
            <Stack gap='md'>
              <Textarea
                label='Reason'
                placeholder='Enter rejection reason'
                minRows={3}
                value={rejectReason}
                onChange={(event) => setRejectReason(event.currentTarget.value)}
                disabled={rejectMasterPlanMutation.isPending}
              />
              <Group justify='flex-end'>
                <Button
                  variant='default'
                  onClick={handleRejectModalClose}
                  disabled={rejectMasterPlanMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  color='red'
                  leftSection={<IconThumbDown size={16} />}
                  onClick={handleRejectSubmit}
                  loading={rejectMasterPlanMutation.isPending}
                >
                  Submit Rejection
                </Button>
              </Group>
            </Stack>
          </Modal>

          {/* Version Overview Modal - Enhanced with Table UI */}
          <Modal
            opened={versionOverviewOpened}
            onClose={closeVersionOverview}
            title={
              <Group gap='sm'>
                <Box
                  style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: '8px',
                    padding: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <IconHistory size={20} color='white' />
                </Box>
                <Text fw={700} fz={18}>
                  Version History Overview
                </Text>
                <Badge color='violet' variant='light' size='lg' radius='md'>
                  {groupedVersionsData.length} masterplan
                  {groupedVersionsData.length === 1 ? '' : 's'}
                </Badge>
              </Group>
            }
            size='95%'
            centered
          >
            <Stack gap='lg'>
              <Paper p='sm' withBorder bg='blue.0'>
                <Text fz={14} c='blue.9'>
                  Compare all versions for each master plan configuration in {jobFamilyGroup} (
                  {year}
                  {endYear ? ` - ${endYear}` : ''}). Values highlighted in yellow indicate changes
                  from the previous version.
                </Text>
              </Paper>

              {groupedVersionsData.length === 0 ? (
                <Paper p='xl' withBorder radius='md' bg='gray.0'>
                  <Stack align='center' gap='md'>
                    <IconHistory size={48} style={{ color: '#adb5bd' }} />
                    <Text fz={16} fw={600} c='dimmed'>
                      No version history found
                    </Text>
                    <Text fz={14} c='dimmed' ta='center'>
                      No version history available for these master plans.
                    </Text>
                  </Stack>
                </Paper>
              ) : (
                <Accordion variant='separated' radius='md' chevronPosition='right'>
                  {groupedVersionsData.map((group, groupIndex) => {
                    const latestVersion = group.latestVersion;
                    const statusColor =
                      statusColorMap[latestVersion.status?.toLowerCase()] || 'gray';
                    const allVersions = group.allVersions || [];

                    return (
                      <Accordion.Item
                        key={`${group.masterplanId}-${groupIndex}`}
                        value={`masterplan-${groupIndex}`}
                        style={{
                          border: '2px solid #e9ecef',
                          borderRadius: '12px',
                          marginBottom: '12px'
                        }}
                      >
                        <Accordion.Control>
                          <Group justify='space-between' wrap='nowrap' pr='md'>
                            <Group gap='md'>
                              <Box>
                                <Badge
                                  size='xl'
                                  radius='md'
                                  variant='gradient'
                                  gradient={{ from: 'blue', to: 'grape' }}
                                >
                                  v{latestVersion.displayVersion || latestVersion.versionNumber}
                                </Badge>
                                <Text fz={10} c='dimmed' ta='center' mt={4}>
                                  Latest
                                </Text>
                              </Box>
                              <Stack gap={2}>
                                <Text fw={700} fz={16}>
                                  {latestVersion.programName}
                                </Text>
                                <Group gap='xs'>
                                  <Badge size='sm' variant='light' color='blue'>
                                    {group.jobFamily}
                                  </Badge>
                                  <Badge size='sm' variant='light' color='gray'>
                                    {group.jobBand}
                                  </Badge>
                                  <Badge size='sm' variant='light' color='grape'>
                                    {group.businessUnit}
                                  </Badge>
                                </Group>
                                <Text fz={12} c='dimmed'>
                                  <Dates date={latestVersion.createdAt} dateFormat='dateNameTime' />
                                </Text>
                              </Stack>
                            </Group>
                            <Group gap='xs'>
                              <Tooltip label='Export version history to Excel' withArrow>
                                <ActionIcon
                                  variant='light'
                                  size='lg'
                                  color='green'
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleExportVersionHistory(group);
                                  }}
                                  radius='md'
                                >
                                  <IconDownload size={18} />
                                </ActionIcon>
                              </Tooltip>
                              <Badge color={statusColor} variant='filled' size='md' radius='md'>
                                {latestVersion.status || 'Pending'}
                              </Badge>
                              <Badge color='violet' variant='light' size='md' radius='md'>
                                {group.approvedVersionCount} approved version
                                {group.approvedVersionCount === 1 ? '' : 's'}
                              </Badge>
                              {group.totalVersions !== group.approvedVersionCount && (
                                <Badge color='red' variant='light' size='md' radius='md'>
                                  {group.totalVersions - group.approvedVersionCount} rejected
                                </Badge>
                              )}
                            </Group>
                          </Group>
                        </Accordion.Control>
                        <Accordion.Panel>
                          <ScrollArea>
                            <Stack gap='lg'>
                              {/* Comparison Table */}
                              <Paper withBorder radius='md' p='md'>
                                <Stack gap='md'>
                                  <Group gap='xs'>
                                    <IconHistory size={18} style={{ color: '#228be6' }} />
                                    <Text fw={700} fz={15} c='blue.9'>
                                      Version Comparison
                                    </Text>
                                  </Group>

                                  <div
                                    style={{
                                      minWidth: '100%',
                                      overflowX: 'auto'
                                    }}
                                  >
                                    {/* Version Numbers Row */}
                                    <div
                                      style={{
                                        display: 'grid',
                                        gridTemplateColumns: `200px repeat(${allVersions.length}, 1fr)`,
                                        gap: '12px',
                                        marginBottom: '8px',
                                        minWidth: `${200 + allVersions.length * 250}px`
                                      }}
                                    >
                                      <Paper withBorder p='sm' style={{ background: '#f8f9fa' }}>
                                        <Text fz={12} fw={700} c='secondary.7' tt='uppercase'>
                                          Field
                                        </Text>
                                      </Paper>
                                      {allVersions.map((version: any, idx: number) => (
                                        <Paper
                                          key={`version-header-${idx}`}
                                          withBorder
                                          p='sm'
                                          style={{
                                            background:
                                              idx === 0
                                                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                                : '#f8f9fa'
                                          }}
                                        >
                                          <Stack gap={4}>
                                            <Badge
                                              size='lg'
                                              radius='md'
                                              variant={idx === 0 ? 'filled' : 'outline'}
                                              color={
                                                idx === 0
                                                  ? 'white'
                                                  : version.isApproved === false
                                                    ? 'red'
                                                    : 'gray'
                                              }
                                              style={{
                                                backgroundColor:
                                                  idx === 0 ? 'rgba(255,255,255,0.2)' : undefined,
                                                color: idx === 0 ? 'white' : undefined,
                                                border: idx === 0 ? '1px solid white' : undefined
                                              }}
                                            >
                                              v{version.displayVersion || version.versionNumber}
                                            </Badge>
                                            {idx === 0 && (
                                              <Text fz={9} c='white' ta='center' fw={600}>
                                                LATEST
                                              </Text>
                                            )}
                                          </Stack>
                                        </Paper>
                                      ))}
                                    </div>

                                    {/* Status Row */}
                                    <div
                                      style={{
                                        display: 'grid',
                                        gridTemplateColumns: `200px repeat(${allVersions.length}, 1fr)`,
                                        gap: '12px',
                                        marginBottom: '8px',
                                        minWidth: `${200 + allVersions.length * 250}px`
                                      }}
                                    >
                                      <Paper withBorder p='sm' style={{ background: '#f8f9fa' }}>
                                        <Text fz={12} fw={600} c='secondary.7'>
                                          Status
                                        </Text>
                                      </Paper>
                                      {allVersions.map((version: any, idx: number) => {
                                        const versionStatus =
                                          statusColorMap[version.status?.toLowerCase()] || 'gray';
                                        return (
                                          <Paper
                                            key={`status-${idx}`}
                                            withBorder
                                            p='sm'
                                            style={{
                                              ...getHighlightStyle(
                                                shouldHighlight(
                                                  'status',
                                                  version.status,
                                                  allVersions,
                                                  idx
                                                )
                                              )
                                            }}
                                          >
                                            <Badge
                                              color={versionStatus}
                                              variant='filled'
                                              size='md'
                                              radius='md'
                                            >
                                              {version.status || 'Pending'}
                                            </Badge>
                                          </Paper>
                                        );
                                      })}
                                    </div>

                                    {/* Created At Row */}
                                    <div
                                      style={{
                                        display: 'grid',
                                        gridTemplateColumns: `200px repeat(${allVersions.length}, 1fr)`,
                                        gap: '12px',
                                        marginBottom: '8px',
                                        minWidth: `${200 + allVersions.length * 250}px`
                                      }}
                                    >
                                      <Paper withBorder p='sm' style={{ background: '#f8f9fa' }}>
                                        <Text fz={12} fw={600} c='secondary.7'>
                                          Created At
                                        </Text>
                                      </Paper>
                                      {allVersions.map((version: any, idx: number) => (
                                        <Paper key={`created-${idx}`} withBorder p='sm'>
                                          <Text fz={12} c='dark.7'>
                                            <Dates
                                              date={version.createdAt}
                                              dateFormat='dateNameTime'
                                            />
                                          </Text>
                                        </Paper>
                                      ))}
                                    </div>

                                    {/* Note Row */}
                                    <div
                                      style={{
                                        display: 'grid',
                                        gridTemplateColumns: `200px repeat(${allVersions.length}, 1fr)`,
                                        gap: '12px',
                                        marginBottom: '24px',
                                        minWidth: `${200 + allVersions.length * 250}px`
                                      }}
                                    >
                                      <Paper withBorder p='sm' style={{ background: '#f8f9fa' }}>
                                        <Text fz={12} fw={600} c='secondary.7'>
                                          Note
                                        </Text>
                                      </Paper>
                                      {allVersions.map((version: any, idx: number) => (
                                        <Paper
                                          key={`note-${idx}`}
                                          withBorder
                                          p='sm'
                                          style={{
                                            ...getHighlightStyle(
                                              shouldHighlight(
                                                'note',
                                                version.note,
                                                allVersions,
                                                idx
                                              )
                                            )
                                          }}
                                        >
                                          <Text fz={12} c='dark.7' lineClamp={3}>
                                            {version.note || '-'}
                                          </Text>
                                        </Paper>
                                      ))}
                                    </div>
                                  </div>
                                </Stack>
                              </Paper>

                              {/* Curriculum Comparison */}
                              {allVersions.some(
                                (v: any) => v.curriculum && v.curriculum.length > 0
                              ) && (
                                <>
                                  <Divider
                                    label={
                                      <Group gap='xs'>
                                        <IconHistory size={16} />
                                        <Text fw={600} fz={14}>
                                          Curriculum Comparison
                                        </Text>
                                      </Group>
                                    }
                                    labelPosition='center'
                                  />

                                  <Stack gap='md'>
                                    {allVersions.map(
                                      (version: any, vIndex: number) =>
                                        version.curriculum &&
                                        version.curriculum.length > 0 && (
                                          <Paper
                                            key={`detail-${version.versionNumber}`}
                                            withBorder
                                            radius='md'
                                            p='md'
                                            bg={vIndex === 0 ? 'blue.0' : 'gray.0'}
                                          >
                                            <Stack gap='sm'>
                                              <Group justify='space-between'>
                                                <Group gap='xs'>
                                                  <Badge
                                                    size='lg'
                                                    radius='md'
                                                    variant={vIndex === 0 ? 'filled' : 'outline'}
                                                    color={
                                                      vIndex === 0
                                                        ? 'blue'
                                                        : version.isApproved === false
                                                          ? 'red'
                                                          : 'gray'
                                                    }
                                                  >
                                                    v
                                                    {version.displayVersion ||
                                                      version.versionNumber}
                                                  </Badge>
                                                  <Text fw={700} fz={14}>
                                                    {version.programName}
                                                  </Text>
                                                  {vIndex === 0 && (
                                                    <Badge size='sm' color='violet' variant='light'>
                                                      Latest
                                                    </Badge>
                                                  )}
                                                  {version.isApproved === false && (
                                                    <Badge size='sm' color='red' variant='light'>
                                                      Rejected
                                                    </Badge>
                                                  )}
                                                </Group>
                                                <Badge color='grape' variant='light' radius='md'>
                                                  {version.curriculum.length} item
                                                  {version.curriculum.length === 1 ? '' : 's'}
                                                </Badge>
                                              </Group>

                                              <Paper withBorder radius='md' p='sm' bg='white'>
                                                <DataTables
                                                  columns={versionCurriculumColumns}
                                                  data={version.curriculum}
                                                  loading={false}
                                                  withPagination={false}
                                                  emptyMessageTitle='No curriculum'
                                                  emptyMessageDescription='No curriculum entries for this version.'
                                                />
                                              </Paper>
                                            </Stack>
                                          </Paper>
                                        )
                                    )}
                                  </Stack>
                                </>
                              )}
                            </Stack>
                          </ScrollArea>
                        </Accordion.Panel>
                      </Accordion.Item>
                    );
                  })}
                </Accordion>
              )}
            </Stack>
          </Modal>
        </Stack>
      </Box>

      {/* Navigation Sidebar */}
      <Box
        style={{
          width: isMobile ? '100%' : '360px',
          flexShrink: 0
        }}
      >
        <Stack gap='md'>
          {/* Job Family Navigation */}
          {showJobFamilyNavigation && (
            <Paper
              withBorder
              radius='lg'
              p='lg'
              shadow='sm'
              style={{
                background: 'linear-gradient(to bottom, #ffffff, #f8f9fa)'
              }}
            >
              <Group gap='xs' mb='lg'>
                <IconHistory size={20} style={{ color: '#228be6' }} />
                <Text fw={700} fz={17} c='blue.7'>
                  Job Families
                </Text>
              </Group>
              {jobFamilyList.length === 1 ? (
                <Paper withBorder radius='md' p='md' bg='blue.0'>
                  <Stack gap={8}>
                    <Text fz={13} fw={600} c='blue.9'>
                      Single Job Family
                    </Text>
                    <Text fz={12} c='dimmed'>
                      This group only has one job family.
                    </Text>
                  </Stack>
                </Paper>
              ) : (
                <>
                  <Text fz={12} c='dimmed' mb='md'>
                    Select a job family to view
                  </Text>
                  <Box
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px',
                      maxHeight: '300px',
                      overflowY: 'auto',
                      paddingRight: '4px'
                    }}
                  >
                    {jobFamilyList.map((jf, index) => {
                      const isSelected = selectedJobFamilyIndex === index;
                      return (
                        <Paper
                          key={index}
                          p='md'
                          withBorder
                          radius='lg'
                          shadow={isSelected ? 'md' : 'xs'}
                          style={{
                            cursor: 'pointer',
                            backgroundColor: isSelected ? '#e7f5ff' : '#ffffff',
                            borderColor: isSelected ? '#228be6' : '#dee2e6',
                            borderWidth: isSelected ? '2px' : '1px',
                            transform: isSelected ? 'scale(1.02)' : 'scale(1)',
                            transition: 'all 0.2s ease'
                          }}
                          onClick={() => {
                            setSelectedJobFamilyIndex(index);
                            setSelectedMasterplanIndex(0);
                          }}
                        >
                          <Group justify='space-between' wrap='nowrap'>
                            <Stack gap={4}>
                              <Text fw={700} fz={15} c={isSelected ? 'blue.9' : 'dark.7'}>
                                {jf.jobFamily}
                              </Text>
                              <Badge size='sm' variant='light' color='gray'>
                                {jf.masterplans?.length || 0} masterplan
                                {jf.masterplans?.length === 1 ? '' : 's'}
                              </Badge>
                            </Stack>
                            {isSelected && (
                              <IconCheck size={18} style={{ color: '#228be6', flexShrink: 0 }} />
                            )}
                          </Group>
                        </Paper>
                      );
                    })}
                  </Box>
                </>
              )}
            </Paper>
          )}

          {/* Masterplan Navigation */}
          {showMasterplanNavigation && (
            <Paper
              withBorder
              radius='lg'
              p='lg'
              shadow='sm'
              style={{
                background: 'linear-gradient(to bottom, #ffffff, #f8f9fa)'
              }}
            >
              <Group gap='xs' mb='lg'>
                <IconHistory size={20} style={{ color: '#228be6' }} />
                <Text fw={700} fz={17} c='blue.7'>
                  Job Band
                </Text>
              </Group>
              {masterplanList.length === 1 ? (
                <Paper withBorder radius='md' p='md' bg='blue.0'>
                  <Stack gap={8}>
                    <Text fz={13} fw={600} c='blue.9'>
                      Single Configuration
                    </Text>
                    <Text fz={12} c='dimmed'>
                      This job family only has one master plan configuration.
                    </Text>
                  </Stack>
                </Paper>
              ) : (
                <>
                  <Text fz={12} c='dimmed' mb='md'>
                    Select a configuration to view details
                  </Text>
                  <Box
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px',
                      maxHeight: '400px',
                      overflowY: 'auto',
                      paddingRight: '4px'
                    }}
                  >
                    {masterplanList.map((masterplan, index) => {
                      const isSelected = selectedMasterplanIndex === index;
                      return (
                        <Paper
                          key={index}
                          p='md'
                          withBorder
                          radius='lg'
                          shadow={isSelected ? 'md' : 'xs'}
                          style={{
                            cursor: 'pointer',
                            backgroundColor: isSelected ? '#e7f5ff' : '#ffffff',
                            borderColor: isSelected ? '#228be6' : '#dee2e6',
                            borderWidth: isSelected ? '2px' : '1px',
                            transform: isSelected ? 'scale(1.02)' : 'scale(1)',
                            transition: 'all 0.2s ease'
                          }}
                          onClick={() => setSelectedMasterplanIndex(index)}
                        >
                          <Stack gap={6}>
                            <Group justify='space-between' wrap='nowrap'>
                              <Text
                                fw={700}
                                fz={15}
                                c={isSelected ? 'blue.9' : 'dark.7'}
                                lineClamp={1}
                              >
                                {masterplan.jobBand}
                              </Text>
                              {isSelected && (
                                <IconCheck size={18} style={{ color: '#228be6', flexShrink: 0 }} />
                              )}
                            </Group>
                            <Text fz={12} c='dimmed' lineClamp={1}>
                              {masterplan.businessUnitName || masterplan.businessUnitId}
                            </Text>
                            <Group gap='xs' mt={4}>
                              <Badge
                                size='sm'
                                radius='md'
                                variant={isSelected ? 'filled' : 'light'}
                                color={
                                  masterplan.status?.toLowerCase() === 'approved'
                                    ? 'teal'
                                    : masterplan.status?.toLowerCase() === 'rejected'
                                      ? 'red'
                                      : 'yellow'
                                }
                              >
                                {masterplan.status || 'Pending'}
                              </Badge>
                              <Badge size='sm' radius='md' variant='light' color='gray'>
                                {masterplan.curriculumList?.length || 0} items
                              </Badge>
                            </Group>
                          </Stack>
                        </Paper>
                      );
                    })}
                  </Box>
                </>
              )}
            </Paper>
          )}
        </Stack>
      </Box>
    </Flex>
  );
};

export default MasterPlanDetailByGroup;
